import { Link } from "react-router-dom";
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import DeleteIcon from '@mui/icons-material/Delete';
import Checkbox from '@mui/material/Checkbox';
import "./table.scss";
import customLocaleText from '../datatable/LocaleText'


export const FilterBar = ({
    searchTerm,
    handleSearchChange,
    filterDate,
    handleFilterChange,
    filterDateRegistro,
    handleFilterChangeDate,
    searchTienda,
    handleTiendaFilterChange,
    filterVigilante,
    handleVigilanteFilterChange,
}) => (
    <div className="filterBarWrapper">
        <div className="filterBar">
            <input
                type="text"
                placeholder="Filtrar por tipo de reporte"
                value={searchTerm}
                onChange={handleSearchChange}
            />
        </div>
        <div className="filterBar">
            <input
                type="text"
                placeholder="Filtrar por fecha de subida"
                value={filterDate}
                onChange={handleFilterChange}
            />
        </div>
        <div className="filterBar">
            <input
                type="text"
                placeholder="Filtrar por fecha de suceso"
                value={filterDateRegistro}
                onChange={handleFilterChangeDate}
            />
        </div>
        <div className="filterBar">
            <input
                type="text"
                placeholder="Filtrar por servicio"
                value={searchTienda}
                onChange={handleTiendaFilterChange}
            />
        </div>
        <div className="filterBar">
            <input
                type="text"
                placeholder="Filtrar por vigilante"
                value={filterVigilante}
                onChange={handleVigilanteFilterChange}
            />
        </div>
    </div>
);

export const ActionButtons = ({ handleDownloadSelected, selectedRows }) => (
    <div className="tableActions">
        <div className="downloadButton">
            <button onClick={handleDownloadSelected} disabled={selectedRows.length === 0}>
                Descargar seleccionados
            </button>
        </div>
    </div>
);

export const Table = ({ filteredRows, allRowsSelected, selectedRows, handleSelectAllRows, handleRowSelection, handleDeleteSelected }) => (
    <DataGridPro
        initialState={{
            columns: {
                columnVisibilityModel: {
                    id: false,
                },
            },
        }}
        className="table"
        pageSize={100}
        rowsPerPageOptions={[20, 100, 500, 1000]}
        slots={{ toolbar: GridToolbar }}
        checkboxSelection
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={(newSelection) => {
            // Si se selecciona/deselecciona el checkbox principal
            if (newSelection.length === filteredRows.length && selectedRows.length !== filteredRows.length) {
                // Seleccionar todas las filas filtradas
                handleSelectAllRows({ target: { checked: true } });
            } else if (newSelection.length === 0 && selectedRows.length > 0) {
                // Deseleccionar todas
                handleSelectAllRows({ target: { checked: false } });
            } else {
                // Selección individual
                const added = newSelection.filter(id => !selectedRows.includes(id));
                const removed = selectedRows.filter(id => !newSelection.includes(id));
                
                // Procesar elementos añadidos
                added.forEach(id => {
                    handleRowSelection({ target: { checked: true } }, id);
                });
                
                // Procesar elementos removidos
                removed.forEach(id => {
                    handleRowSelection({ target: { checked: false } }, id);
                });
            }
        }}
        columns={[
            {
                field: "id",
                hide: true, // Oculta la columna en la vista
            },
            {
                field: "documentTypeId",
                headerName: "Tipo de reporte",
                flex: 1,
                valueGetter: (params) => {
                    // Campos predefinidos
                    const predefinedIds = ['112', 'denuncia', 'ims', 'intervencion', 'legal', 'parte de trabajo'];

                    // Verifica si el valor de documentTypeId está en tus predefinidos
                    if (predefinedIds.includes(params.row.documentTypeId)) {
                        return params.row.documentTypeId;
                    } else {
                        // Si no es un valor predefinido, muestra documentTypeName
                        return params.row.documentTypeName || 'Sin tipo definido';
                    }
                },
            },
            {
                field: "fecha",
                headerName: "Fecha subida",
                flex: 1,
                valueGetter: (params) => {
                    const fecha = params.row.fecha;
                    if (fecha != null && fecha !== '') {
                        const str = typeof fecha === 'string' ? fecha : (fecha instanceof Date ? fecha.toLocaleDateString('es-ES') : String(fecha));
                        if (str.includes("/")) {
                            const dateParts = str.split("/");
                            dateParts[0] = dateParts[0].length === 1 ? "0" + dateParts[0] : dateParts[0];
                            dateParts[1] = dateParts[1].length === 1 ? "0" + dateParts[1] : dateParts[1];
                            return dateParts.join("/");
                        }
                        return str;
                    }
                    return params.row.fecha ?? '';
                },
                sortComparator: (v1, v2) => {
                    const s1 = String(v1 ?? "");
                    const s2 = String(v2 ?? "");
                    if (!s1 || !s2) return s1.localeCompare(s2);
                    const dateParts1 = s1.split("/").reverse().join("");
                    const dateParts2 = s2.split("/").reverse().join("");
                    return dateParts1.localeCompare(dateParts2);
                },
            },
            {
                field: "fields.FECHA",
                headerName: "Fecha de suceso",
                flex: 1,
                valueGetter: (params) => {
                    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                    if (isSafari) return params.row.fecha ?? '';

                    const fecha = params.row.fields?.FECHA;
                    if (fecha == null || fecha === '') return params.row.fecha ?? '';

                    let date;
                    if (typeof fecha === 'string') {
                        if (!fecha.includes("-")) return params.row.fecha ?? '';
                        const dateParts = fecha.split("-");
                        date = new Date(`${dateParts[0]}-${dateParts[1]}-${dateParts[2]}`);
                    } else if (fecha instanceof Date) {
                        date = fecha;
                    } else if (typeof fecha === 'number') {
                        date = new Date(fecha);
                    } else if (typeof fecha === 'object') {
                        if (typeof fecha.toDate === 'function') {
                            date = fecha.toDate();
                        } else if (typeof fecha.seconds === 'number') {
                            date = new Date(fecha.seconds * 1000 + (fecha.nanoseconds || 0) / 1000000);
                        } else if (fecha.$date) {
                            date = new Date(fecha.$date);
                        } else {
                            return params.row.fecha ?? '';
                        }
                    } else {
                        return params.row.fecha ?? '';
                    }

                    if (!date || Number.isNaN(date.getTime())) return params.row.fecha ?? '';
                    const dia = date.getDate();
                    const mes = date.getMonth() + 1;
                    const año = date.getFullYear();
                    return `${dia < 10 ? '0' : ''}${dia}/${mes < 10 ? '0' : ''}${mes}/${año}`;
                },
                sortComparator: (v1, v2) => {
                    const s1 = String(v1 ?? "");
                    const s2 = String(v2 ?? "");
                    if (!s1 || !s2) return s1.localeCompare(s2);
                    const dateParts1 = s1.split("/").reverse().join("");
                    const dateParts2 = s2.split("/").reverse().join("");
                    return dateParts1.localeCompare(dateParts2);
                },
            },
            {
                field: "tipVS",
                headerName: "TIP",
                flex: 1,
                // valueGetter: (params) => params.row.vigilante.replace('@gmail.com', ''), //cambiar esto para arreglar el bug
            },
            {
                field: "nombre",
                headerName: "Nombre",
                flex: 1,
            },
            {
                field: "vigilante",
                headerName: "Email",
                flex: 1,
                // valueGetter: (params) => params.row.vigilanteEmail || ''
            },
            {
                field: "tienda",
                headerName: "Servicio",
                flex: 1,
            },
            {
                field: "cliente",
                headerName: "Cliente",
                flex: 1,
            },
            {
                field: "actions",
                headerName: "Acciones",
                width: 200,
                renderCell: (params) => (
                    <div className="cellAction">
                        <Link to={`/vigilantes/details/${params.id}`}>
                            <div className="viewButton">Ver</div>
                        </Link>
                        <DeleteIcon className="deleteIcon" onClick={handleDeleteSelected} disabled={selectedRows.length === 0} />
                    </div>
                ),
            },
        ]}
        rows={filteredRows}
        autoHeight
        disableSelectionOnClick
        localeText={customLocaleText}
        getRowClassName={(params) => {
            const isDownloaded = params.row.downloaded === true;

            // Devuelve la clase de estilo en función de si downloaded es undefined, false o true
            if (isDownloaded) {
                return 'downloadedRow';
            } else if (params.row.downloaded === false) {
                return 'notDownloadedRow';
            } else {
                return 'notDownloadedRow'; // Otra clase para el caso de downloaded === undefined
            }
        }}
    />
);

export const DateFilterDropdown = ({ dateFilter, onChange }) => (
    <div className="dateFilterDropdown">
        <label className="dateFilter" htmlFor="dateFilter">Mostrar documentos de los últimos:</label>
        <select id="dateFilter" value={dateFilter} onChange={onChange}>
            <option value="all">Todos</option>
            <option value="last3Days">Últimos 3 días</option>
            <option value="lastWeek">Última semana</option>
            <option value="today">Hoy</option>
        </select>
    </div>
);